/**
 * Bulk-apply manual service info to many accounts in one click.
 *
 * Built for phone-only customers whose Hub service profiles the Hub API
 * cannot serve yet (it only authorizes by email). Instead of typing
 * container / frequency per customer, staff select every account missing
 * service info and apply the values once. Only fields filled in are
 * applied — nothing is mass-cleared.
 */
import { useMemo, useState } from 'react';
import {
  X, Layers, Loader2, CheckCircle2, AlertCircle,
} from 'lucide-react';

import { adminApi } from '@/lib/api';

const FIELDS = [
  { key: 'container_size', label: 'Container size', placeholder: 'e.g. 8-yard' },
  { key: 'collection_frequency', label: 'Collection frequency', placeholder: 'e.g. Weekly · Tue' },
  { key: 'waste_stream', label: 'Waste stream', placeholder: 'e.g. MSW' },
  { key: 'next_collection_at', label: 'Next scheduled pickup', placeholder: 'e.g. 2026-10-09 or Tue Oct 9' },
  { key: 'service_notes', label: 'Service notes', placeholder: 'Optional', textarea: true },
];

export default function AdminBulkServiceInfoModal({ customers, onClose, onApplied }) {
  // Candidates: accounts with no container/frequency data at all.
  const candidates = useMemo(
    () =>
      (customers || []).filter(
        (c) => c.account_id && !c.container_size && !c.collection_frequency,
      ),
    [customers],
  );
  const [selected, setSelected] = useState(
    () => new Set(candidates.map((c) => c.account_id)),
  );
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const toggle = (aid) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(aid)) n.delete(aid);
      else n.add(aid);
      return n;
    });
  const allOn = candidates.length > 0 && selected.size === candidates.length;
  const toggleAll = () =>
    setSelected(
      allOn ? new Set() : new Set(candidates.map((c) => c.account_id)),
    );

  const filledCount = FIELDS.filter(({ key }) => (draft[key] || '').trim()).length;

  const apply = async () => {
    if (busy || selected.size === 0 || filledCount === 0) return;
    setBusy(true);
    setFlash(null);
    try {
      const fields = {};
      FIELDS.forEach(({ key }) => {
        const v = (draft[key] || '').trim();
        if (v) fields[key] = v;
      });
      const { data } = await adminApi.bulkApplyServiceInfo([...selected], fields);
      setFlash({
        type: 'success',
        msg: `Applied to ${data.updated} account${data.updated === 1 ? '' : 's'}. Customers see it on their next dashboard load.`,
      });
      onApplied?.();
    } catch (err) {
      setFlash({ type: 'error', msg: err?.detail || 'Bulk apply failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="admin-bulk-service-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EDE5D4]">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-[#0B1930]" />
            <div>
              <h2 className="font-heading uppercase tracking-wide text-base text-[#0B1930]">
                Bulk Service Info
              </h2>
              <p className="text-[11px] text-[#4B5563]">
                Apply container / schedule to many accounts at once
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-[#4B5563] hover:text-[#0B1930] disabled:opacity-40"
            data-testid="admin-bulk-service-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {flash && (
            <div
              className={`mb-3 rounded-xl px-3 py-2 text-sm flex items-start gap-2 ${
                flash.type === 'success'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
              data-testid="admin-bulk-service-flash"
            >
              {flash.type === 'success'
                ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
              <span>{flash.msg}</span>
            </div>
          )}

          <div className="space-y-4">
            {FIELDS.map(({ key, label, placeholder, textarea }) => (
              <div key={key}>
                <label className="block text-[11px] uppercase tracking-wide text-[#4B5563] mb-1">
                  {label}
                </label>
                {textarea ? (
                  <textarea
                    rows={2}
                    value={draft[key] || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full rounded-xl bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                    data-testid={`admin-bulk-service-input-${key}`}
                  />
                ) : (
                  <input
                    type="text"
                    value={draft[key] || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full rounded-full bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                    data-testid={`admin-bulk-service-input-${key}`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-xl border border-[#EDE5D4] bg-[#FAF7F0] px-4 py-3">
            <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-[#4B5563]">
              <input
                type="checkbox"
                checked={allOn}
                onChange={toggleAll}
                className="accent-[#0B1930]"
                data-testid="admin-bulk-service-select-all"
              />
              Accounts missing service info ({selected.size} of {candidates.length} selected)
            </label>
            <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
              {candidates.length === 0 && (
                <p className="text-xs text-[#9CA3AF]">
                  Every account already has service info — nothing to fill.
                </p>
              )}
              {candidates.map((c) => (
                <label
                  key={c.account_id}
                  className="flex items-center gap-2 text-sm text-[#0B1930]"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(c.account_id)}
                    onChange={() => toggle(c.account_id)}
                    className="accent-[#0B1930]"
                    data-testid={`admin-bulk-service-row-${c.account_id}`}
                  />
                  <span className="truncate">
                    {c.business_name}
                    {c.service_address ? ` — ${c.service_address}` : ''}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#EDE5D4] bg-[#FAF7F0]">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#0B1930] hover:bg-[#EDE5D4] transition-colors disabled:opacity-60"
            data-testid="admin-bulk-service-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={busy || selected.size === 0 || filledCount === 0}
            className="inline-flex items-center gap-2 rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
            data-testid="admin-bulk-service-apply"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Layers className="h-3.5 w-3.5" />}
            Apply to {selected.size} account{selected.size === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}
